import type { TestimonialsFormValues } from 'src/routes/TestimonialsForm/types';
import { flowAsync } from 'src/utils/fp';
import { cond } from 'lodash/fp';
import type {
  postTestimonialsCreateResponse,
  postTestimonialsLinkResponse,
} from '@lt/api/dist/methods/testimonials/types';
import type { AxiosError } from 'axios';
import {
  ifClientAuth,
  ifClientIsNotAuth,
  ifTestimonialsLinkNotSuccess,
  ifTestimonialsLinkSuccess,
} from 'src/routes/TestimonialsForm/utils';
import type { CLIENT } from '@lt/api/dist/types/client';
import { useSelector } from 'react-redux';
import { getClientUnconfirmedPhone } from 'src/store/client/selectors';
import type { UseTestimonialsLink } from './useTestimonialsLink';
import type { UseTestimonialsCreate } from './useTestimonialsCreate';
import type { UseTestimonialsFormTypes } from './useTestimonialsFormTypes';
import type { UseTestimonialsFormCache } from './useTestimonialsFormCache';

type Props = {
  currentClient: null | CLIENT;
} & Pick<
  UseTestimonialsFormCache,
  'saveFormValueCache' | 'resetFormValueCache'
> &
  UseTestimonialsCreate &
  Pick<UseTestimonialsLink, 'handleLinkClientToTestimonials'> &
  Pick<UseTestimonialsFormTypes, 'setFormTypes'>;

export const useTestimonialsForm = ({
  currentClient,
  saveFormValueCache,
  handleTestimonialsCreate,
  handleLinkClientToTestimonials,
  setFormTypes,
  setTestimonialsCreateResponse,
  testimonialsCreateResponse,
  resetFormValueCache,
}: Props) => {
  const unconfirmedPhone = useSelector(getClientUnconfirmedPhone);

  const handleSubmit = async (values: TestimonialsFormValues) => {
    try {
      await flowAsync(
        saveFormValueCache,
        handleTestimonialsCreate,
        cond<
          postTestimonialsCreateResponse,
          Promise<postTestimonialsLinkResponse | AxiosError> | undefined
        >([
          ifClientAuth(
            currentClient,
            unconfirmedPhone,
            handleLinkClientToTestimonials,
            setFormTypes,
          ),
          ifClientIsNotAuth(
            currentClient,
            unconfirmedPhone,
            setFormTypes,
            setTestimonialsCreateResponse,
          ),
        ]),
        cond<postTestimonialsLinkResponse, undefined>([
          ifTestimonialsLinkSuccess(setFormTypes),
          ifTestimonialsLinkNotSuccess(setFormTypes),
        ]),
      )(values);
    } catch (e) {
      console.dir(e);
    }
  };

  const handleSignUp = async (client: CLIENT) => {
    try {
      await flowAsync(
        cond<
          postTestimonialsCreateResponse,
          Promise<postTestimonialsLinkResponse | AxiosError> | undefined
        >([
          ifClientAuth(
            client,
            unconfirmedPhone,
            handleLinkClientToTestimonials,
            setFormTypes,
          ),
          ifClientIsNotAuth(
            client,
            unconfirmedPhone,
            setFormTypes,
            setTestimonialsCreateResponse,
          ),
        ]),
        cond<postTestimonialsLinkResponse, undefined>([
          ifTestimonialsLinkSuccess(setFormTypes),
        ]),
        resetFormValueCache,
      )(testimonialsCreateResponse);
    } catch (e) {
      console.log(e);
    }
  };

  return {
    handleSubmit,
    handleSignUp,
  };
};
